import { useState, useCallback } from "react";
import { useBoxDrawState } from "./useBoxDrawState";
import type { ProductSelection } from "@/types/coverage";

export type BoxCategory = "oos_confirmed" | "empty_area" | "unassigned";

/**
 * Extends useBoxDrawState with product selection tracking, box categorization,
 * and per-box planogram slot assignment for coverage tasks.
 */
export const useCoverageState = () => {
  const boxDrawState = useBoxDrawState();
  const [productSelections, setProductSelections] = useState<
    Map<string, ProductSelection>
  >(new Map());
  const [pendingSelectionBoxKey, setPendingSelectionBoxKey] = useState<
    string | null
  >(null);
  const [boxCategories, setBoxCategories] = useState<
    Map<string, BoxCategory>
  >(new Map());
  /** Maps box key → planogram slot index (aisle index). Default is 0. */
  const [boxPlanogramSlot, setBoxPlanogramSlot] = useState<
    Map<string, number>
  >(new Map());

  /** Build a stable key for a box: "auto-{index}" or "user-{box.id}" */
  const getBoxKey = useCallback(
    (index: number, isUserDrawn: boolean) => {
      if (isUserDrawn) {
        const box = boxDrawState.userDrawnBoxes[index];
        return box ? `user-${box.id}` : `user-idx-${index}`;
      }
      return `auto-${index}`;
    },
    [boxDrawState.userDrawnBoxes],
  );

  /** Get the category of a box. Auto-detections default to 'unassigned'. */
  const getBoxCategory = useCallback(
    (boxKey: string): BoxCategory => {
      return boxCategories.get(boxKey) ?? "unassigned";
    },
    [boxCategories],
  );

  const removeProductSelection = useCallback((boxKey: string) => {
    setProductSelections((prev) => {
      const next = new Map(prev);
      next.delete(boxKey);
      return next;
    });
  }, []);

  /** Move a box to a different category. Clears product selection for non-OOS categories. */
  const moveBoxToCategory = useCallback(
    (boxKey: string, category: BoxCategory) => {
      setBoxCategories((prev) => {
        const next = new Map(prev);
        next.set(boxKey, category);
        return next;
      });
      // Clear product selection when moving away from OOS confirmed
      if (category !== "oos_confirmed") {
        removeProductSelection(boxKey);
      }
    },
    [removeProductSelection],
  );

  /**
   * Toggle a product in the selection for a box.
   * If product is not selected, add it. If already selected, remove it.
   * Does NOT dismiss the pending selection - user must explicitly confirm.
   */
  const toggleProductInSelection = useCallback(
    (
      boxKey: string,
      productId: string,
      planogramId: string,
      bayIndex: number,
      emptyAreaIndex: number,
    ) => {
      setProductSelections((prev) => {
        const next = new Map(prev);
        const existing = prev.get(boxKey);

        if (existing) {
          const currentIds = existing.product_ids;
          const isSelected = currentIds.includes(productId);

          if (isSelected) {
            // Remove product from selection
            const newIds = currentIds.filter((id) => id !== productId);
            if (newIds.length === 0) {
              // No products left, remove the selection entirely
              next.delete(boxKey);
            } else {
              next.set(boxKey, { ...existing, product_ids: newIds });
            }
          } else {
            // Add product to selection
            next.set(boxKey, {
              ...existing,
              product_ids: [...currentIds, productId],
            });
          }
        } else {
          // New selection
          next.set(boxKey, {
            empty_area_index: emptyAreaIndex,
            product_ids: [productId],
            planogram_id: planogramId,
            bay_index: bayIndex,
          });
        }

        return next;
      });
    },
    [],
  );

  /** Check if a product is selected for a box */
  const isProductSelected = useCallback(
    (boxKey: string, productId: string): boolean => {
      const selection = productSelections.get(boxKey);
      return selection?.product_ids.includes(productId) ?? false;
    },
    [productSelections],
  );

  /** Get all selected product IDs for a box */
  const getSelectedProductIds = useCallback(
    (boxKey: string): string[] => {
      return productSelections.get(boxKey)?.product_ids ?? [];
    },
    [productSelections],
  );

  /** Get count of selected products for a box */
  const getSelectedProductCount = useCallback(
    (boxKey: string): number => {
      return productSelections.get(boxKey)?.product_ids.length ?? 0;
    },
    [productSelections],
  );

  /** Confirm the current selection and dismiss the popup */
  const confirmProductSelection = useCallback(() => {
    setPendingSelectionBoxKey(null);
  }, []);

  /** Request product selection for a box (shows the popup). */
  const requestProductSelection = useCallback((boxKey: string) => {
    setPendingSelectionBoxKey(boxKey);
  }, []);

  const dismissProductSelection = useCallback(() => {
    setPendingSelectionBoxKey(null);
  }, []);

  // --- Planogram slot assignment ---

  /** Assign a box to a planogram slot (aisle index). */
  const assignBoxToSlot = useCallback(
    (boxKey: string, slotIndex: number) => {
      setBoxPlanogramSlot((prev) => {
        const next = new Map(prev);
        next.set(boxKey, slotIndex);
        return next;
      });
    },
    [],
  );

  /** Get the planogram slot for a box (default 0). */
  const getBoxSlot = useCallback(
    (boxKey: string): number => {
      return boxPlanogramSlot.get(boxKey) ?? 0;
    },
    [boxPlanogramSlot],
  );

  /** Get all box keys assigned to a specific slot. */
  const getBoxesForSlot = useCallback(
    (slotIndex: number): string[] => {
      const result: string[] = [];
      boxPlanogramSlot.forEach((slot, key) => {
        if (slot === slotIndex) result.push(key);
      });
      // Also include keys not in the map (default slot 0)
      if (slotIndex === 0) {
        // Any box not explicitly assigned is in slot 0
        // Caller should check if boxKey is missing from the map
      }
      return result;
    },
    [boxPlanogramSlot],
  );

  /** Clear product selections for all boxes in a given slot. */
  const clearSelectionsForSlot = useCallback(
    (slotIndex: number) => {
      setProductSelections((prev) => {
        const next = new Map(prev);
        for (const [key] of next) {
          if ((boxPlanogramSlot.get(key) ?? 0) === slotIndex) {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [boxPlanogramSlot],
  );

  /** Wrap box delete to also remove product selection, category, and slot assignment. */
  const handleBoxDelete = useCallback(
    (index: number, isUserDrawn: boolean = false) => {
      const key = getBoxKey(index, isUserDrawn);
      removeProductSelection(key);
      setBoxCategories((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      setBoxPlanogramSlot((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      boxDrawState.handleBoxDelete(index, isUserDrawn);
    },
    [boxDrawState.handleBoxDelete, getBoxKey, removeProductSelection],
  );

  /** Get count of OOS Confirmed boxes missing product selections. */
  const getMissingSelectionsCount = useCallback(
    (existingBoxCount: number) => {
      let total = 0;
      let missing = 0;

      // Only count OOS Confirmed boxes as requiring product selections
      for (let i = 0; i < existingBoxCount; i++) {
        if (!boxDrawState.deletedBoxIndices.has(i)) {
          const key = getBoxKey(i, false);
          if (getBoxCategory(key) === "oos_confirmed") {
            total++;
            const selection = productSelections.get(key);
            // Check that at least one product is selected
            if (!selection || selection.product_ids.length === 0) missing++;
          }
        }
      }

      boxDrawState.userDrawnBoxes.forEach((box, i) => {
        if (box.type !== "draw") return;
        const key = getBoxKey(i, true);
        if (getBoxCategory(key) === "oos_confirmed") {
          total++;
          const selection = productSelections.get(key);
          // Check that at least one product is selected
          if (!selection || selection.product_ids.length === 0) missing++;
        }
      });

      return { total, missing };
    },
    [
      boxDrawState.deletedBoxIndices,
      boxDrawState.userDrawnBoxes,
      productSelections,
      getBoxKey,
      getBoxCategory,
    ],
  );

  /** Count how many non-deleted existing boxes are still unassigned. */
  const getUnassignedCount = useCallback(
    (existingBoxCount: number) => {
      let count = 0;
      for (let i = 0; i < existingBoxCount; i++) {
        if (!boxDrawState.deletedBoxIndices.has(i)) {
          const key = getBoxKey(i, false);
          if (getBoxCategory(key) === "unassigned") count++;
        }
      }
      return count;
    },
    [boxDrawState.deletedBoxIndices, getBoxKey, getBoxCategory],
  );

  const canSubmit = useCallback(
    (existingBoxCount: number) => {
      const { missing } = getMissingSelectionsCount(existingBoxCount);
      const unassigned = getUnassignedCount(existingBoxCount);
      return missing === 0 && unassigned === 0;
    },
    [getMissingSelectionsCount, getUnassignedCount],
  );

  const resetCoverageState = useCallback(() => {
    boxDrawState.resetBoxDrawState();
    setProductSelections(new Map());
    setPendingSelectionBoxKey(null);
    setBoxCategories(new Map());
    setBoxPlanogramSlot(new Map());
  }, [boxDrawState.resetBoxDrawState]);

  return {
    ...boxDrawState,
    handleBoxDelete,
    productSelections,
    pendingSelectionBoxKey,
    getBoxKey,
    toggleProductInSelection,
    isProductSelected,
    getSelectedProductIds,
    getSelectedProductCount,
    confirmProductSelection,
    removeProductSelection,
    requestProductSelection,
    dismissProductSelection,
    canSubmit,
    getMissingSelectionsCount,
    getUnassignedCount,
    resetCoverageState,
    boxCategories,
    getBoxCategory,
    moveBoxToCategory,
    // Planogram slot tracking
    boxPlanogramSlot,
    assignBoxToSlot,
    getBoxSlot,
    getBoxesForSlot,
    clearSelectionsForSlot,
    // Bulk setters (for admin result initialization)
    setBoxCategories,
    setProductSelections,
    setBoxPlanogramSlot,
  };
};
